import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ClipboardList,
  Loader2,
  RefreshCw,
  Sparkles,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import IntakeField from "@/components/agreements/IntakeField";
import { INTAKE_SECTIONS, INTAKE_FIELD_BY_ID, isAnsweredField } from "@/data/agreements/intakeSchema";
import { useAgreementProject } from "@/hooks/useAgreementProject";
import { eventsApi } from "@/lib/agreements/api";

/**
 * ProjectIntake — route: /agreements/intake
 *
 * A sectioned wizard over INTAKE_SECTIONS. The founder answers once; every recommendation,
 * checklist level and template blank in the module is driven from these answers.
 *
 * The live preview is the point of the page: the plan is derived from the intake on every
 * keystroke, so the document count moves while they answer.
 */

/** Blank-only test — matches missingRequired(), which is what gates the Finish button. */
const isBlank = (v) => (Array.isArray(v) ? v.length === 0 : String(v ?? "").trim() === "");

export default function ProjectIntake() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Every visit to /agreements/intake without a ?project= param starts a
  // fresh submission so brands never overwrite each other. `replace: true`
  // keeps this out of history so Back doesn't ping-pong.
  useEffect(() => {
    if (!params.get("project")) {
      const fresh = `proj_${(crypto.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2, 8))}`;
      setParams({ project: fresh }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    projectId,
    intake,
    setIntake,
    saveIntake,
    completeness,
    missingRequired,
    plan,
    savePlan,
    loading,
    saving,
    error,
  } = useAgreementProject();

  const [step, setStep] = useState(0);
  const [showMissing, setShowMissing] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const total = INTAKE_SECTIONS.length;
  const section = INTAKE_SECTIONS[Math.min(step, total - 1)] || INTAKE_SECTIONS[0];
  const isLast = step >= total - 1;
  const busy = submitting || saving;

  useEffect(() => {
    eventsApi.track({ type: "start_intake", projectId });
  }, [projectId]);

  // Per-section answered counts for the rail.
  const sectionStats = useMemo(
    () =>
      INTAKE_SECTIONS.map((s) => {
        // "answered" ignores fields still sitting on their schema default, so the rail agrees
        // with the percentage in the header; "missing" stays blank-only, so it agrees with the
        // required-field check that gates Finish.
        const answered = s.fields.filter((f) => isAnsweredField(f, intake[f.id])).length;
        const missing = s.fields.filter((f) => f.required && isBlank(intake[f.id])).length;
        return { id: s.id, answered, total: s.fields.length, missing };
      }),
    [intake],
  );

  const missingLabels = missingRequired.map((id) => ({
    id,
    label: INTAKE_FIELD_BY_ID[id]?.label || id,
    section: INTAKE_FIELD_BY_ID[id]?.section,
  }));

  const goToSection = (sectionId) => {
    const idx = INTAKE_SECTIONS.findIndex((s) => s.id === sectionId);
    if (idx >= 0) setStep(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goStep = (next) => {
    setStep(Math.max(0, Math.min(total - 1, next)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setField = (fieldId, value) => {
    setIntake({ [fieldId]: value });
    if (showMissing) setShowMissing(false);
  };

  const finish = async () => {
    if (missingRequired.length > 0) {
      setShowMissing(true);
      toast.error(`Still needed: ${missingLabels.map((m) => m.label).join(", ")}`);
      if (missingLabels[0]?.section) goToSection(missingLabels[0].section);
      return;
    }
    setSubmitting(true);
    setSaveFailed(false);
    try {
      await saveIntake(); // emits complete_intake on a successful save
      try {
        await savePlan(); // emits generate_plan
      } catch {
        toast.error("Your answers are saved. We could not store the generated plan — it is rebuilt from your answers, so nothing is lost.");
      }
      toast.success(`Plan ready — ${plan.counts.total} document${plan.counts.total === 1 ? "" : "s"} for your brand.`);
      // Route to the detail plan for THIS specific submission so it shows
      // the founder's just-submitted brand and not some other project.
      navigate(`/agreements/plan?project=${encodeURIComponent(projectId)}`);
    } catch {
      // The hook only emits complete_intake when the backend save succeeds, so emit it here
      // for the failure path. Exactly one complete_intake per finish, either way.
      eventsApi.track({ type: "complete_intake", projectId, saved: false, completeness });
      setSaveFailed(true);
      toast.error("We could not save to your account. Your answers are kept on this device.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "Project intake" }]}
      title="Project & brand intake"
      subtitle="Answer once. These answers decide which agreements you actually need, set the level on every checklist row, and fill in the blanks in every template you generate."
      actions={
        <Button asChild variant="outline">
          <Link to="/agreements/plan">
            <ClipboardList className="mr-2 h-4 w-4" /> My plan
          </Link>
        </Button>
      }
    >
      {loading && (
        <p className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your saved answers…
        </p>
      )}

      {/* Progress header */}
      <div className="mb-5 rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              Section {step + 1} of {total} · {section.title}
            </p>
            <p className="text-xs text-muted-foreground">{completeness}% of the intake answered</p>
          </div>
          <Progress value={completeness} className="h-2 w-full sm:w-56" />
        </div>
      </div>

      {saveFailed && (
        <div className="mb-5 rounded-xl border border-pink-300 bg-pink-50 p-4 text-sm text-pink-900">
          <p className="flex items-center gap-2 font-semibold">
            <WifiOff className="h-4 w-4" /> We could not reach your account
          </p>
          <p className="mt-1.5 text-pink-900/90">
            Nothing is lost — every answer is kept on this device and your plan is generated from them locally. Retry the
            save, or carry on and save later.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={finish} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Retry save
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/agreements/plan")}>
              Continue to my plan anyway
            </Button>
          </div>
          {error ? <p className="mt-2 text-xs text-pink-900/70">{String(error?.message || error)}</p> : null}
        </div>
      )}

      {/* Mobile / tablet: collapsible live preview above the form */}
      <div className="mb-5 xl:hidden">
        <button
          type="button"
          onClick={() => setPreviewOpen((v) => !v)}
          aria-expanded={previewOpen}
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-left"
        >
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-indigo-900">
            <Sparkles className="h-4 w-4 shrink-0 text-indigo-600" />
            <span className="truncate">
              {plan.counts.total} document{plan.counts.total === 1 ? "" : "s"} in your plan so far
            </span>
          </span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 text-indigo-700 transition-transform", previewOpen && "rotate-180")} />
        </button>
        {previewOpen && <LivePreview plan={plan} className="mt-3" />}
      </div>

      <div className="grid gap-6 lg:grid-cols-[190px_minmax(0,1fr)] xl:grid-cols-[190px_minmax(0,1fr)_300px]">
        {/* Section rail (desktop) */}
        <nav className="hidden lg:block" aria-label="Intake sections">
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sections</p>
          <ol className="space-y-1">
            {INTAKE_SECTIONS.map((s, i) => {
              const stat = sectionStats[i];
              const active = i === step;
              const complete = stat.answered === stat.total;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => goToSection(s.id)}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted",
                      active && "bg-muted font-medium text-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px]",
                        complete
                          ? "border-emerald-300 bg-emerald-100 text-emerald-700"
                          : active
                            ? "border-indigo-400 bg-indigo-600 text-white"
                            : "border-border text-muted-foreground",
                      )}
                    >
                      {complete ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block leading-tight">{s.title}</span>
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">
                        {stat.answered}/{stat.total} answered
                        {stat.missing > 0 && <span className="text-pink-600"> · {stat.missing} required</span>}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* The form */}
        <div className="min-w-0">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold">{section.title}</h2>
                {section.blurb && <p className="mt-1 text-sm text-muted-foreground">{section.blurb}</p>}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {section.fields.map((field) => (
                  <IntakeField
                    key={field.id}
                    field={field}
                    value={intake[field.id]}
                    onChange={(v) => setField(field.id, v)}
                    className={cn(
                      (field.type === "textarea" || field.type === "multiselect" || field.type === "radio") &&
                        "sm:col-span-2",
                    )}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {showMissing && missingLabels.length > 0 && (
            <div className="mt-4 rounded-xl border border-pink-300 bg-pink-50 p-4 text-sm text-pink-900">
              <p className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> {missingLabels.length} answer
                {missingLabels.length === 1 ? " is" : "s are"} still needed
              </p>
              <ul className="mt-2 space-y-1.5">
                {missingLabels.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center gap-2">
                    <span>{m.label}</span>
                    {m.section && (
                      <button type="button" onClick={() => goToSection(m.section)} className="underline underline-offset-2">
                        go to it
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Wizard controls */}
          <div className="sticky bottom-0 z-10 mt-4 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:mx-0 lg:rounded-b-xl lg:px-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                {isLast
                  ? missingRequired.length > 0
                    ? `${missingRequired.length} required answer${missingRequired.length === 1 ? "" : "s"} still missing.`
                    : "Everything required is answered."
                  : "Nothing here is locked in — you can come back and change any answer."}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => goStep(step - 1)} disabled={step === 0 || busy}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                {isLast ? (
                  <Button onClick={finish} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {busy ? "Saving…" : "Finish & build my plan"}
                  </Button>
                ) : (
                  <Button onClick={() => goStep(step + 1)} disabled={busy}>
                    Next <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky live preview (large desktop) */}
        <div className="hidden xl:block">
          <div className="sticky top-4">
            <LivePreview plan={plan} />
          </div>
        </div>
      </div>
    </AgreementsShell>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Live preview — the plan recomputing as they answer                         */
/* ────────────────────────────────────────────────────────────────────────── */
function LivePreview({ plan, className }) {
  const counts = plan?.counts || { required: 0, recommended: 0, situational: 0, total: 0 };
  const prev = useRef(null);
  const [delta, setDelta] = useState(0);

  useEffect(() => {
    if (prev.current === null) {
      prev.current = counts.total;
      return;
    }
    const d = counts.total - prev.current;
    prev.current = counts.total;
    if (d === 0) return;
    setDelta(d);
    const t = setTimeout(() => setDelta(0), 2600);
    return () => clearTimeout(t);
  }, [counts.total]);

  const topRequired = (plan?.items || []).filter((i) => i.level === "required").slice(0, 5);

  return (
    <Card className={cn("border-indigo-200 bg-indigo-50/60", className)}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-indigo-900">Your plan, live</h2>
          {delta !== 0 && (
            <Badge className="bg-indigo-600 text-white hover:bg-indigo-600">
              {delta > 0 ? `+${delta}` : delta} document{Math.abs(delta) === 1 ? "" : "s"}
            </Badge>
          )}
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold tabular-nums leading-none text-indigo-900">{counts.total}</span>
          <span className="text-sm text-indigo-900/80">document{counts.total === 1 ? "" : "s"}</span>
        </div>

        {/* Chips — auto-fit so they sit 3-across when the sidebar is wide
            enough, and stack one-per-line when it isn't. `min(100%, 110px)`
            guarantees each chip is at least wide enough to fit the longest
            label ("RECOMMENDED") on a single line, so the text never has
            to break mid-word. */}
        <dl className="mt-3 grid gap-2 text-center [grid-template-columns:repeat(auto-fit,minmax(min(100%,112px),1fr))]">
          {[
            ["Required", counts.required, "text-red-700 bg-red-50 border-red-200"],
            ["Recommended", counts.recommended, "text-pink-700 bg-pink-50 border-pink-200"],
            ["Situational", counts.situational, "text-slate-700 bg-slate-50 border-slate-200"],
          ].map(([label, value, tone]) => (
            <div key={label} className={cn("rounded-lg border px-2 py-2 min-w-0", tone)}>
              <dd className="text-lg font-semibold tabular-nums leading-none">{value}</dd>
              <dt className="mt-1 text-[10px] font-medium uppercase leading-tight tracking-wide whitespace-nowrap">{label}</dt>
            </div>
          ))}
        </dl>

        {topRequired.length > 0 ? (
          <div className="mt-4">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-900/70">
              Required for you right now
            </p>
            <ul className="space-y-1 text-xs text-indigo-950">
              {topRequired.map((item) => (
                <li key={item.docId} className="flex gap-1.5">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-indigo-500" />
                  <span className="min-w-0">{item.doc?.name || item.docId}</span>
                </li>
              ))}
            </ul>
            {counts.required > topRequired.length && (
              <p className="mt-1.5 text-[11px] text-indigo-900/70">
                + {counts.required - topRequired.length} more required
              </p>
            )}
          </div>
        ) : (
          <p className="mt-4 text-xs text-indigo-900/80">
            Answer a few questions and your document list builds itself here.
          </p>
        )}

        <p className="mt-4 border-t border-indigo-200 pt-3 text-[11px] leading-relaxed text-indigo-900/70">
          This updates on every answer. Nothing is generated by a model — each document is added by a rule you can read on
          the plan page.
        </p>
      </CardContent>
    </Card>
  );
}
